import React, { useRef, useState } from "react";
import qr from "../../assets/scan_me_qr_code.jpg";
import Banner from "../Banner";
import { useLocation } from "react-router-dom";
import { fileTypeVaidation } from "./fileTypeValidation";
import { toast, ToastContainer } from "react-toastify";
import apiendspoint from "../../apiendpoint.json";
import { serviceDetails } from "../../pages/ServicesPage.jsx";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Pay = () => {
  const query = useQuery();
  const servicename = query.get("service");
  const price = query.get("price");
  const duration = query.get("duration");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [ahname, setAhName] = useState("");
  const [phone, setPhone] = useState("");
  const [txnId, setTxnId] = useState("");
  const [proof, setProof] = useState("");
  const [service, setService] = useState(servicename || "");
  const [serviceDuration, setServiceDuration] = useState(duration || "--");
  const [servicePrice, setServicePrice] = useState(price || "000");
  const [pan, setPan] = useState("");
  const [loader, setLoader] = useState(false);
  const proofRef = useRef(null);

  const [durationOptions, setDurationOptions] = useState(["--"]);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) {
      toast.error("Please enter numbers only.");
      return;
    }
    if (value.length > 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setPhone(value);
  };

  const updateDurationOptions = (selectedService) => {
    const serviceData = serviceDetails.find(
      (ser) => ser.heading === selectedService
    );

    if (serviceData) {
      const availableDurations = ["--"];

      // Check each possible duration and add if price exists
      serviceData.prices.forEach(priceObj => {
        const durationType = Object.keys(priceObj)[0];
        const priceValue = priceObj[durationType];

        if (priceValue && !priceValue.includes("null") && !availableDurations.includes(durationType)) {
          availableDurations.push(durationType);
        }
      });

      setDurationOptions(availableDurations);
      setServiceDuration("--"); // Reset to default when service changes
      setServicePrice("000");
    } else {
      setDurationOptions(["--"]);
      setServiceDuration("--");
    }
  };

  const handleServiceChange = (event) => {
    const selectedService = event.target.value;
    setService(selectedService);
    updateDurationOptions(selectedService);
  };

  const handleDurationChange = (event) => {
    const selectedDuration = event.target.value;
    setServiceDuration(selectedDuration);

    // Update service price based on selected duration
    const serviceData = serviceDetails.find((s) => s.heading === service);
    if (serviceData && selectedDuration !== "--") {
      const priceObj = serviceData.prices.find(p => p[selectedDuration]);
      if (priceObj) {
        const priceValue = priceObj[selectedDuration].replace(/\D/g, ''); // Extract numbers only
        setServicePrice(priceValue || "000");
      } else {
        setServicePrice("000");
      }
    } else {
      setServicePrice("000");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true);
    try {
      const formData = {
        name,
        emailId: email,
        mobileNumber: phone,
        paymentProof: proof,
        accountHolderName: ahname,
        transactionId: txnId,
        service,
        serviceDuration,
        servicePrice,
        panno: pan,
      };

      if (!servicePrice || servicePrice === "000") {
        toast.error("Please select a valid service duration to get the price");
        return;
      }
      if (!service) {
        toast.error("Please select a service");
        return;
      }
      if (!serviceDuration.trim() || serviceDuration === "--") {
        toast.error("Please select a service duration");
        return;
      }
      if (proofRef.current) {
        if (proofRef.current.value.split(".")[1] === "pdf") {
          toast.error("Please select image Only");
          proofRef.current.value = "";
          return;
        }
      }
      if (!pan.trim()) {
        toast.error("Pan number is required");
        return;
      }

      let response = await fetch(
        `${apiendspoint.equityUrl}/nextapi/PurchaseDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Something Went Wrong");
      }

      let data = await response.json();
      if (data) {
        toast.success("Purchase Initiated Successfully.");
      }

      if (response.ok) {
        setName("");
        setEmail("");
        setPhone("");
        setPan("");
        setTxnId("");
        setAhName("");
        setServicePrice("000");
        setServiceDuration("--");
        setService("");
        if (proofRef.current) {
          proofRef.current.value = "";
        }
        setProof("");
      }
    } catch (error) {
      toast.error("Something Went Wrong");
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <section className="w-full my-10 md:my-20 lg:max-w-6xl lg:mx-auto p-4 py-10 bg-gradient-to-br from-[#01295c] via-slate-900 to-[#000000] shadow-2xl space-y-6 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-blue-600/10 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-blue-800/10 blur-3xl"></div>

        {/* Glowing dots */}
        <div className="absolute top-1/4 left-1/4 w-3 h-3 rounded-full bg-blue-400 animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-2 h-2 rounded-full bg-blue-300 animate-pulse"></div>

        {/* Form header */}
        <div className="text-center mb-8 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Payment Verification</h2>
          <p className="text-blue-100 max-w-2xl mx-auto">
            Complete your subscription by verifying your payment details
          </p>
        </div>

        <div className="w-full py-6 px-4 flex flex-col lg:flex-row gap-8 flex-wrap-reverse md:flex-nowrap justify-center relative z-10">
          <form onSubmit={handleSubmit} className="flex-1 space-y-6 text-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Email <sup className="text-red-500">*</sup>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                    placeholder="Your Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <div className="absolute right-3 top-3 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Name <sup className="text-red-500">*</sup>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                    placeholder="Your Full Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <div className="absolute right-3 top-3 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Account Holder Name */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Account Holder Name <sup className="text-red-500">*</sup>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                  placeholder="Account Holder Name"
                  value={ahname}
                  onChange={(e) => setAhName(e.target.value)}
                  required
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Phone <sup className="text-red-500">*</sup>
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                    placeholder="Your Phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    required
                  />
                  <div className="absolute right-3 top-3 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Transaction ID */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Transaction ID <sup className="text-red-500">*</sup>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                  placeholder="Transaction ID"
                  value={txnId}
                  onChange={(e) => setTxnId(e.target.value)}
                  required
                />
              </div>

              {/* Payment Proof */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Payment Proof <sup className="text-red-500">*</sup> (img/png/jpg)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    ref={proofRef}
                    onChange={async (e) => {
                      setProof(await fileTypeVaidation(e, "image"));
                    }}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 transition-all"
                    accept="image/*"
                    required
                  />
                </div>
              </div>

              {/* Service */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Service <sup className="text-red-500">*</sup>
                </label>
                <select
                  onChange={handleServiceChange}
                  value={service}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none"
                  required
                >
                  <option value="" disabled>Select A Service</option>
                  {serviceDetails.map((serv, i) => (
                    <option key={i} value={serv.heading}>
                      {serv.heading.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service Duration */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Service Duration <sup className="text-red-500">*</sup>
                </label>
                <select
                  value={serviceDuration}
                  onChange={handleDurationChange}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none"
                  required
                >
                  {durationOptions.map((value, i) => (
                    <option key={i} value={value}>
                      {value.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pan */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Pan <sup className="text-red-500">*</sup>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                  placeholder="Pan Number"
                  value={pan}
                  onChange={(e) => setPan(e.target.value)}
                  required
                />
              </div>

              {/* Price */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-blue-100">
                  Price <sup className="text-red-500">*</sup>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={servicePrice === "000" ? "Select duration first" : `₹${servicePrice}`}
                  disabled
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 text-center">
              <button
                type="submit"
                className="px-8 py-3 font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300 transform hover:scale-105"
                disabled={loader}
              >
                {!loader ? (
                  <span className="flex items-center justify-center gap-2">
                    Submit Payment
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                )}
              </button>
            </div>
          </form>

          {/* QR Code Section */}
          {/* <div className="md:w-96 h-full border-2 border-blue-500/30 p-4 rounded-xl bg-gradient-to-br from-blue-900/20 to-blue-800/20 backdrop-blur-sm flex flex-col items-center justify-center">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-blue-100 mb-2">Scan to Pay</h3>
              <p className="text-blue-200 text-sm">Use any UPI app to complete payment</p>
            </div>
            <img
              src='/nexteraPaymentQR.png'
              alt="QR Code"
              className="w-64 h-64 object-contain border-2 border-blue-400/20 rounded-lg"
            />
            <div className="mt-4 text-center">
              <p className="text-blue-100 text-sm font-medium">UPI ID: yespay.bizsbiz118420@yesbankltd</p>
              <p className="text-blue-200 text-xs mt-2">After payment, fill the form to verify</p>
            </div>
          </div> */}
        </div>

        <ToastContainer
          autoClose={3000}
          limit={2}
          hideProgressBar={true}
          className="z-[9999] fixed top-28"
        />
      </section>

      <Banner />
    </>
  );
};

export default Pay;